import React, { useState } from 'react';
import { Alert, AlertTitle, Button, Stack, Box } from '@mui/material';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { RouteTo } from '@routing/RouteMap';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import { useSubscriptionAccess } from '@company/hooks/useSubscriptionAccess';

const GET_CHECKOUT_STATE = gql`
  query GetCheckoutState {
    savedCheckoutState
  }
`;

/**
 * Banner-Komponente für das Dashboard zur Fortsetzung unterbrochener Checkout-Prozesse.
 * 
 * Diese Komponente zeigt eine Benachrichtigung im Dashboard an, wenn der Benutzer
 * einen gespeicherten Checkout-Zustand hat und ermutigt ihn, den Kauf fortzusetzen.
 * 
 * Funktionalität:
 * - Prüft automatisch auf gespeicherte Checkout-Zustände
 * - Zeigt ansprechende Benachrichtigung mit Produktanzahl
 * - Direkter Link zum Shop zur Fortsetzung
 * - Kann vom Benutzer dauerhaft ausgeblendet werden
 * - Respektiert Benutzerentscheidungen (localStorage)
 */
const CheckoutContinuationBanner: React.FC = () => {
  const navigate = useNavigate();
  const { hasAccess, loading: subscriptionLoading } = useSubscriptionAccess();
  const [isDismissed, setIsDismissed] = useState(() => {
    // Prüfe ob Banner bereits dauerhaft ausgeblendet wurde
    return localStorage.getItem('checkoutBannerDismissed') === 'true';
  });

  const { data, loading } = useQuery(GET_CHECKOUT_STATE, {
    // Nur laden wenn Banner nicht ausgeblendet ist
    skip: isDismissed,
    errorPolicy: 'ignore' // Fehler nicht anzeigen, da dies optional ist
  });

  // Prüfe ob gespeicherter Checkout-Zustand existiert
  const hasCheckoutState = data?.savedCheckoutState?.selectedProducts?.length > 0;
  const productCount = data?.savedCheckoutState?.selectedProducts?.length || 0;

  // Banner nicht anzeigen wenn:
  // - Noch lädt
  // - Bereits ausgeblendet
  // - Kein gespeicherter Zustand vorhanden
  if (loading || subscriptionLoading || hasAccess || isDismissed || !hasCheckoutState) {
    return null;
  }

  const handleContinueCheckout = () => {
    console.log('🛒 Benutzer setzt Checkout vom Dashboard aus fort');
    navigate(RouteTo.SHOP, {
      state: {
        fromDashboardBanner: true,
        restoredProductCount: productCount
      }
    });
  };

  const handleDismiss = () => {
    console.log('🙈 Checkout-Banner temporär ausgeblendet');
    setIsDismissed(true);
  };

  const handleDismissPermanently = () => {
    console.log('🙈 Checkout-Banner dauerhaft ausgeblendet');
    localStorage.setItem('checkoutBannerDismissed', 'true');
    setIsDismissed(true);
  };

  return (
    <Alert 
      severity="warning" 
      icon={<ShoppingCartIcon />}
      sx={{ 
        mb: 2,
        '& .MuiAlert-action': {
          alignItems: 'flex-start',
          pt: 0
        }
      }}
      action={
        <Button
          size="small"
          onClick={handleDismiss}
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      }
    >
      <AlertTitle>Checkout fortsetzen</AlertTitle>
      <Box sx={{ mb: 1 }}>
        Du hast {productCount} Produkt{productCount > 1 ? 'e' : ''} in deinem Warenkorb. 
        Möchtest du deinen Kauf abschließen?
      </Box>
      
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button 
          size="small" 
          variant="contained"
          color="warning"
          startIcon={<ShoppingCartIcon />}
          onClick={handleContinueCheckout}
        >
          Checkout fortsetzen
        </Button>
      </Stack>
    </Alert>
  );
};

export default CheckoutContinuationBanner; 